import { DateTime } from '@ksyos/date-time';
import { OrderedMap, Record } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import type { IMedication } from '../../../../../interfaces/medication';
import { fetchJson } from '../../../../../../store/fetch';
import { ProfessionalSummaryDataAction } from '../../../../professionalSummaryData/ProfessionalSummaryDataSection';

const medicationRecordType = Record({
    name: null,
    dosageInstruction: null,
    quantityValue: null,
    mkcodeCode: null,
    mkcodeSystem: null,
    mkcodeSystemName: null,
    dosageValue: null,
    dosageUnit: null,
    quantityDisplayName: null,
    quantityCode: null,
    quantityCodeSystem: null,
    medInstrCode: null,
    medInstrSystem: null,
    medInstrName: null,
    startDate: null,
    endDate: null,
    action: ProfessionalSummaryDataAction.add,
});

export class MedicationRecord extends medicationRecordType {
    public readonly id: string;
    public readonly name: string | null;
    public readonly dosageInstruction: string | null;
    public readonly quantityValue: string | null;
    public readonly mkcodeCode: string | null;
    public readonly mkcodeSystem: string | null;
    public readonly mkcodeSystemName: string | null;
    public readonly dosageValue: string | null;
    public readonly dosageUnit: string | null;
    public readonly quantityDisplayName: string | null;
    public readonly quantityCode: string | null;
    public readonly quantityCodeSystem: string | null;
    public readonly medInstrCode: string | null;
    public readonly medInstrSystem: string | null;
    public readonly medInstrName: string | null;
    public readonly startDate: string | null;
    public readonly endDate: string | null;
    public readonly action: ProfessionalSummaryDataAction;

    constructor(values?: any) {
        super(values);
    }
}

export type Medications = OrderedMap<string, MedicationRecord>;

export async function loadRequestMedications(
    patient: {
        authorization?: { canSeePatientMedication?: boolean; canSeeProfessionalSummaryDataDetails?: boolean };
        id: string;
    },
    requestId?: string
) {
    let medications: Medications = await loadPatientMedication(patient, Boolean(requestId));

    if (requestId) {
        const requestMedicationsResponse = await fetchJson(`/api/requests/${requestId}/requestMedications`, 'GET');
        for (const requestMedication of requestMedicationsResponse.json.items) {
            const key = makeMedicationKey(requestMedication);
            if (medications.has(key)) {
                medications = medications.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
            } else {
                medications = medications.set(uuidv4(), new MedicationRecord(requestMedication));
            }
        }
    }

    return medications;
}

export async function loadProcedureMedications(
    patient: {
        authorization?: { canSeePatientMedication?: boolean; canSeeProfessionalSummaryDataDetails?: boolean };
        id: string;
    },
    procedureId?: string
) {
    let medications: Medications = await loadPatientMedication(patient, Boolean(procedureId));

    if (procedureId) {
        const procedureMedicationsResponse = await fetchJson(
            `/api/procedures/${procedureId}/procedureMedications`,
            'GET'
        );
        for (const procedureMedication of procedureMedicationsResponse.json.items) {
            const key = makeMedicationKey(procedureMedication);
            if (medications.has(key)) {
                medications = medications.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
            } else {
                medications = medications.set(uuidv4(), new MedicationRecord(procedureMedication));
            }
        }
    }

    return medications;
}

export async function loadProcessActivityMedications(
    patient: {
        authorization?: { canSeePatientMedication?: boolean };
        id: string;
    },
    processActivityId?: string
) {
    let medications: Medications = await loadPatientMedication(patient, Boolean(processActivityId));

    if (processActivityId) {
        const processActivityMedicationsResponse = await fetchJson(
            `/api/processActivities/${processActivityId}/medications`,
            'GET'
        );

        for (const processActivityMedication of processActivityMedicationsResponse.json.items) {
            const key = makeMedicationKey(processActivityMedication);

            if (medications.has(key)) {
                medications = medications.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
            } else {
                medications = medications.set(uuidv4(), new MedicationRecord(processActivityMedication));
            }
        }
    }

    return medications;
}

async function loadPatientMedication(
    patient: {
        authorization?: { canSeePatientMedication?: boolean };
        id: string;
    },
    isEditMedications?: boolean
) {
    let medications: Medications = OrderedMap();
    if (patient.authorization?.canSeePatientMedication) {
        const patientMedicationsResponse = await fetchJson(`/api/patients/${patient.id}/medications`, 'GET');
        for (const patientMedication of patientMedicationsResponse.json.items) {
            const action =
                !isEditMedications && isCurrentMedication(patientMedication)
                    ? ProfessionalSummaryDataAction.pick
                    : ProfessionalSummaryDataAction.skip;
            medications = medications.set(
                makeMedicationKey(patientMedication),
                new MedicationRecord({ ...patientMedication, action })
            );
        }
    }

    return medications;
}

function makeMedicationKey(medication: IMedication) {
    return (
        medication.name +
        '\u0000' +
        medication.dosageInstruction +
        '\u0000' +
        medication.quantityValue +
        '\u0000' +
        medication.mkcodeCode +
        '\u0000' +
        medication.mkcodeSystem +
        '\u0000' +
        medication.mkcodeSystemName +
        '\u0000' +
        medication.dosageValue +
        '\u0000' +
        medication.dosageUnit +
        '\u0000' +
        medication.quantityDisplayName +
        '\u0000' +
        medication.quantityCode +
        '\u0000' +
        medication.quantityCodeSystem +
        '\u0000' +
        medication.medInstrCode +
        '\u0000' +
        medication.medInstrSystem +
        '\u0000' +
        medication.medInstrName +
        '\u0000' +
        medication.startDate +
        '\u0000' +
        medication.endDate
    );
}

export function filterAndPrepareMedicationsForSaving(medications?: Medications, skipAddMedications?: boolean) {
    if (!medications) {
        return [];
    }

    const medicationToSave: IMedication[] = medications
        .valueSeq()
        .filter((medication) => {
            if (!medication) {
                return false;
            } else if (medication.action === ProfessionalSummaryDataAction.add) {
                if (!skipAddMedications) {
                    return !!(
                        medication.name ||
                        medication.dosageInstruction ||
                        medication.startDate ||
                        medication.endDate
                    );
                }

                return true;
            } else {
                return medication.action === ProfessionalSummaryDataAction.pick;
            }
        })
        .toJS();

    return medicationToSave;
}

export function isCurrentMedication(medication: { endDate: string | null; startDate: string | null }) {
    if (medication.startDate && DateTime.now().isBefore(medication.startDate)) {
        return false;
    }

    if (medication.endDate && DateTime.now().isAfter(medication.endDate)) {
        return false;
    }

    return true;
}
